import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  BookOpen,
  Building2,
  CreditCard,
  FileEdit,
  Layers,
  Library,
  Mail,
  ShieldCheck,
  Terminal,
} from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';

const cardClass =
  'flex flex-col rounded-xl border border-border bg-surface-card p-5 shadow-sm transition hover:border-primary/40 hover:shadow-md';

export function AdminDashboard() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';

  const { data: catalogMeta, isLoading: metaLoading } = useQuery({
    queryKey: ['admin-catalog-meta', tenantId],
    queryFn: () => api.getCatalogMeta(tenantId),
    enabled: !!user?.authenticated,
  });

  const sys = catalogMeta?.counts?.systems_active;
  const varc = catalogMeta?.counts?.variants_active;
  const ver = catalogMeta?.catalog_version;

  return (
    <div>
      <SeoMeta
        path="/admin"
        noindex
        title="Platformbeheer"
        description="CalqTool™ Platformbeheer: intern dashboard voor de hoogste platformrechten (superadmin) — meldingen, CMS, analytics, SEO, library en tenants."
      />
      <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }]} />
      <h1 className="mb-2 text-2xl font-bold text-text">Platformbeheer — overzicht</h1>
      <p className="mb-8 max-w-2xl text-sm text-text-muted">
        Dit gebied is alleen zichtbaar met rol <strong className="text-text">superadmin</strong>. Snel naar CMS, analytics,
        SEO en het API-overzicht. Paginaweergaven tellen na cookie-opt-in; tool-gebruik (WOZ, subsidies, scenario) wordt
        server-side gelogd. Zie ook{' '}
        <code className="rounded bg-surface px-1 text-xs">docs/ADMIN_ANALYTICS_FASE8.md</code>.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Link to="/admin/cms" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <FileEdit className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">CMS</span>
          </div>
          <p className="text-sm text-text-muted">Contentblokken en teksten beheren.</p>
        </Link>
        <Link to="/admin/analytics" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <BarChart3 className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Analytics</span>
          </div>
          <p className="text-sm text-text-muted">
            KPI’s, registratiebronnen, regio/typologie, tool-gebruik — periode instelbaar + CSV-export.
          </p>
        </Link>
        <Link to="/admin/library-coverage" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <Layers className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Library-dekking</span>
          </div>
          <p className="text-sm text-text-muted">
            Hoofdgroepen 10–71: systemen, variantdiepte, histogram en redactionele beoordeling — live merged catalog.
          </p>
        </Link>
        <Link to="/admin/billing" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <CreditCard className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Facturatie</span>
          </div>
          <p className="text-sm text-text-muted">
            Proefperiode, status en tellers handmatig bijwerken. Voor self-serve abonnementen (Stripe):{' '}
            <Link to="/account/billing" className="font-medium text-primary hover:underline">
              Account → Facturatie
            </Link>
            .
          </p>
        </Link>
        <Link to="/admin/tenants" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <Building2 className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Tenants</span>
          </div>
          <p className="text-sm text-text-muted">Alle organisaties met billing- en abonnementsstatus.</p>
        </Link>
        <Link to="/admin/uitnodigingen" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <Mail className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Uitnodigingen</span>
          </div>
          <p className="text-sm text-text-muted">
            Overzicht van teamuitnodigingen bij klanten. Je krijgt ook een e-mail bij elke nieuwe uitnodiging en
            acceptatie.
          </p>
        </Link>
        <Link to="/admin/api-tools" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <Terminal className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">API-overzicht</span>
          </div>
          <p className="text-sm text-text-muted">Routes die de app gebruikt — alleen voor beheerders.</p>
        </Link>
        <Link to="/admin/contract-status" className={`${cardClass} no-underline`}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Contractstatus</span>
          </div>
          <p className="text-sm text-text-muted">Interne checklist API-/productcontract (23 punten).</p>
        </Link>
        <div className={cardClass}>
          <div className="mb-3 flex items-center gap-2 text-primary">
            <Library className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Bibliotheek (tenant)</span>
          </div>
          {metaLoading && <p className="text-sm text-text-muted">Laden…</p>}
          {!metaLoading && catalogMeta && (
            <ul className="space-y-1 text-sm text-text-muted">
              <li>
                <strong className="text-text">Actieve systemen:</strong> {sys ?? '—'}
              </li>
              <li>
                <strong className="text-text">Actieve varianten:</strong> {varc ?? '—'}
              </li>
              {ver && (
                <li>
                  <strong className="text-text">Catalogusversie:</strong> {ver}
                </li>
              )}
            </ul>
          )}
          {!metaLoading && !catalogMeta && (
            <p className="text-sm text-text-muted">Geen meta beschikbaar (inloggen vereist).</p>
          )}
          <p className="mt-3 text-xs text-text-muted">
            Publieke uitleg: <Link to="/help/bibliotheek" className="font-medium text-primary hover:underline">help/bibliotheek</Link>
          </p>
        </div>
        <div className={cardClass}>
          <div className="mb-3 flex items-center gap-2 text-text-muted">
            <BookOpen className="h-5 w-5" aria-hidden />
            <span className="font-semibold text-text">Documentatie (repo)</span>
          </div>
          <p className="text-sm text-text-muted">
            Checklist Fase 8:{' '}
            <code className="rounded bg-surface px-1 text-xs text-text">docs/ADMIN_ANALYTICS_FASE8.md</code>
          </p>
        </div>
      </div>
    </div>
  );
}
